/**
 * RideShare — BlaBlaCar-style intercity carpool.
 *
 * A single page with 3 tabs:
 *   • Search — passengers pick from/to/date, list trips, tap → detail modal → book & pay.
 *   • Publish — drivers fill a full trip form + save recurring template + 1-tap re-publish.
 *   • Mine — my trips (as driver) & bookings (as passenger).
 *
 * Payments are always routed through the FindPay drawer against `fpm_platform`.
 * On payment success we call POST /rideshare/bookings/{id}/pay with the txn id.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Car, Users, Calendar, Search, ArrowRight, PlusCircle, Repeat, Briefcase, MapPin, Coins, ShieldCheck, X, Star, Sparkles, CheckCircle2, Clock, XCircle, Flag, AlertTriangle, Send, Phone } from "lucide-react";
import FindPayCheckout from "@/components/FindPayCheckout";
import ReviewSection from "@/components/ReviewSection";

const TABS = [
  { id: "search",  label: "Search",  Icon: Search },
  { id: "publish", label: "Publish", Icon: PlusCircle },
  { id: "alerts",  label: "Alerts",  Icon: MapPin },
  { id: "mine",    label: "Mine",    Icon: Users },
];

function fmtDate(iso) { try { return new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } }
function fmtINR(n)    { try { return `₹${Number(n).toLocaleString("en-IN")}`; } catch { return `₹${n}`; } }

export default function RideShare() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || "search";
  const setTab = (t) => setParams({ tab: t });
  return (
    <div className="min-h-screen bg-stone-50 pb-24">
      <Header />
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <div className="grid grid-cols-4 gap-1 bg-white rounded-2xl p-1 shadow-sm">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`rideshare-tab-${t.id}`}
              className={`flex flex-col items-center py-2 rounded-xl text-xs transition ${active === t.id ? "bg-emerald-600 text-white shadow-md" : "text-stone-500 hover:bg-stone-100"}`}>
              <t.Icon size={18} /><span className="mt-0.5">{t.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-4">
          {active === "search"  && <SearchPanel  user={user} />}
          {active === "publish" && <PublishPanel user={user} onDone={() => setTab("mine")} />}
          {active === "alerts"  && <AlertsPanel  user={user} />}
          {active === "mine"    && <MinePanel    user={user} />}
        </div>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="bg-gradient-to-br from-emerald-700 via-teal-700 to-emerald-800 text-white px-4 pt-6 pb-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80"><Car size={14} /> Find2Near</div>
        <div className="mt-2 flex items-center gap-2 text-3xl font-bold" data-testid="rideshare-title">
          <Car size={28} /> RideShare
        </div>
        <div className="text-sm opacity-90 mt-1">Share your route. Split the cost. Reach anywhere in India.</div>
      </div>
    </div>
  );
}

// ─── Search ──────────────────────────────────────────────────────────────
function SearchPanel({ user }) {
  const [from, setFrom]   = useState("");
  const [to, setTo]       = useState("");
  const [date, setDate]   = useState("");
  const [women, setWomen] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [minRating, setMinRating]       = useState(0);           // 0 | 4
  const [sort, setSort]                 = useState("depart_at"); // depart_at | rating | price
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const run = async (more = false) => {
    setLoading(true); setFailed(false);
    try {
      const { data } = await api.get("/rideshare/trips", { params: {
        from_city: from || undefined, to_city: to || undefined,
        date: date || undefined, women_only: women || undefined,
        verified_only: verifiedOnly || undefined,
        min_rating:    minRating || undefined,
        sort, offset: more ? (items || []).length : 0,
      }});
      setItems(more ? [...(items || []), ...(data.items || [])] : (data.items || []));
      setMeta({ total: data.total, has_more: data.has_more });
    } catch (e) { toast.error("Search failed"); if (!more) setItems(null); setFailed(true); }
    finally { setLoading(false); }
  };
  return (
    <div>
      <div className="bg-white rounded-2xl p-4 shadow-sm space-y-2" data-testid="rideshare-search-form">
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="From city" value={from} onChange={e => setFrom(e.target.value)} data-testid="rideshare-search-from" />
          <Input placeholder="To city"   value={to}   onChange={e => setTo(e.target.value)}   data-testid="rideshare-search-to" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="rideshare-search-date" />
          <select value={sort} onChange={e => setSort(e.target.value)} className="h-10 px-3 rounded-md border border-input text-sm" data-testid="rideshare-search-sort">
            <option value="depart_at">Sort · Earliest</option>
            <option value="rating">Sort · Top-rated first</option>
            <option value="price">Sort · Cheapest first</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-stone-600">
          <label className="flex items-center gap-1.5 rounded-full border px-3 py-1 cursor-pointer">
            <input type="checkbox" checked={women} onChange={e => setWomen(e.target.checked)} data-testid="rideshare-search-women"/>
            Women-only
          </label>
          <label className={`flex items-center gap-1.5 rounded-full border px-3 py-1 cursor-pointer ${verifiedOnly ? "bg-emerald-50 border-emerald-300" : ""}`} data-testid="rideshare-search-verifiedonly-label">
            <input type="checkbox" checked={verifiedOnly} onChange={e => setVerifiedOnly(e.target.checked)} data-testid="rideshare-search-verifiedonly"/>
            <ShieldCheck size={12} className="text-emerald-600"/> Verified only
          </label>
          <label className={`flex items-center gap-1.5 rounded-full border px-3 py-1 cursor-pointer ${minRating >= 4 ? "bg-amber-50 border-amber-300" : ""}`}>
            <input type="checkbox" checked={minRating >= 4} onChange={e => setMinRating(e.target.checked ? 4 : 0)} data-testid="rideshare-search-4star"/>
            <Star size={12} className="fill-amber-500 text-amber-500"/> 4★+ only
          </label>
        </div>
        <Button onClick={() => run()} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="rideshare-search-submit">
          <Search size={16} className="mr-2" /> Search rides
        </Button>
      </div>

      <div className="mt-4">
        {items === null && !failed && (
          <div className="text-center text-stone-400 text-sm py-8">
            Pick a route + date and tap Search.
          </div>
        )}
        {failed && !loading && (
          <div className="text-center text-sm py-8 space-y-2" data-testid="rideshare-search-error">
            <div className="text-red-600">Search failed. This is not an empty result.</div>
            <button onClick={() => run()} className="h-9 px-4 rounded-full border font-bold" data-testid="rideshare-search-retry">Retry</button>
          </div>
        )}
        {loading && <div className="text-center text-stone-400 text-sm py-8">Searching…</div>}
        {items && items.length === 0 && !loading && (
          <div className="text-center text-stone-400 text-sm py-8">
            No rides match this search. Loosen the trust filters or widen the dates.
          </div>
        )}
        {items && items.length > 0 && (
          <div className="space-y-2" data-testid="rideshare-search-results">
            {meta && <div className="text-xs text-stone-500" data-testid="rideshare-search-count">Showing {items.length} of {meta.total} upcoming rides</div>}
            {items.map(t => <TripCard key={t.trip_id} t={t} onOpen={() => setSelected(t)} />)}
            {meta?.has_more && !loading && (
              <button onClick={() => run(true)} className="w-full h-10 rounded-full border text-sm font-bold" data-testid="rideshare-search-more">Load more</button>
            )}
          </div>
        )}
      </div>

      {selected && <TripDetailModal trip={selected} onClose={() => setSelected(null)} onBooked={() => { setSelected(null); run(); }} />}
    </div>
  );
}

function TripCard({ t, onOpen }) {
  const rating = t.driver_avg_rating;
  return (
    <button onClick={onOpen} className="w-full text-left bg-white rounded-2xl p-3 shadow-sm hover:shadow-md transition" data-testid={`rideshare-card-${t.trip_id}`}>
      <div className="flex items-center justify-between text-xs text-stone-500">
        <span className="flex items-center gap-1"><Clock size={12}/>{fmtDate(t.depart_at)}</span>
        <div className="flex items-center gap-1">
          {t.women_only && <Badge className="bg-pink-100 text-pink-700 text-[10px]">Women-only</Badge>}
          {t.driver_verified && (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid={`rideshare-verified-${t.trip_id}`}>
              <ShieldCheck size={9}/>Verified
            </span>
          )}
        </div>
      </div>
      <div className="mt-1 flex items-center gap-2 font-bold text-stone-900">
        <span className="truncate">{t.from_city}</span>
        <ArrowRight size={14} className="text-emerald-600" />
        <span className="truncate">{t.to_city}</span>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs text-stone-600">
        <span className="flex items-center gap-1.5">
          <span>{t.driver_name}</span>
          {(rating != null || t.driver_ride_count > 0) && (
            <span className="inline-flex items-center gap-0.5 text-emerald-800 font-bold" data-testid={`rideshare-score-${t.trip_id}`}>
              {rating != null && <><Star size={10} className="fill-amber-500 text-amber-500"/> {rating}</>}
              {t.driver_ride_count > 0 && <span className="text-stone-500 ml-0.5">· {t.driver_ride_count} ride{t.driver_ride_count>1?"s":""}</span>}
            </span>
          )}
          <span className="text-stone-400">·</span>
          <span>{t.car_make || "Car"} {t.car_model || ""}</span>
          <span className="text-stone-400">·</span>
          <span>{t.seats_available} seat{t.seats_available!==1?"s":""} left</span>
        </span>
        <span className="text-emerald-700 font-black text-base">{fmtINR(t.price_per_seat_inr)}<span className="text-[10px] font-semibold text-stone-500">/seat</span></span>
      </div>
    </button>
  );
}

function TripDetailModal({ trip, onClose, onBooked }) {
  const { user } = useAuth();
  const [seats, setSeats] = useState(1);
  const [pickup, setPickup] = useState("");
  const [drop, setDrop]     = useState("");
  const [note, setNote]     = useState("");
  const [busy, setBusy]     = useState(false);
  const [booking, setBooking] = useState(null);   // returned by /book
  const [fpOpen, setFpOpen] = useState(false);
  const amountPaise = seats * trip.price_per_seat_inr * 100;

  const book = async () => {
    if (!user) { toast.error("Sign in to book"); return; }
    if (seats > trip.seats_available) { toast.error(`Only ${trip.seats_available} seat(s) left`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/rideshare/trips/${trip.trip_id}/book`, {
        seats, pickup_address: pickup, drop_address: drop, note,
      });
      setBooking(data);
      setFpOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); }
    finally { setBusy(false); }
  };
  const [justPaidBookingId, setJustPaidBookingId] = useState(null);
  const onPaid = async (txn) => {
    setFpOpen(false);
    try {
      await api.post(`/rideshare/bookings/${booking.booking_id}/pay`, { findpay_txn_id: txn.txn_id });
      toast.success(trip.instant_book ? "Seat confirmed! Ride on." : "Payment received — awaiting driver approval.");
      setJustPaidBookingId(booking.booking_id);          // triggers the auto-share modal
      onBooked?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment recorded but booking sync failed."); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="rideshare-detail-modal">
      <div className="bg-white rounded-3xl max-w-md w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-t-3xl">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-80">{fmtDate(trip.depart_at)}</div>
              <div className="mt-1 text-2xl font-bold flex items-center gap-2">
                {trip.from_city} <ArrowRight size={20}/> {trip.to_city}
              </div>
              {trip.via_cities?.length > 0 && (
                <div className="text-xs mt-1 opacity-80">via {trip.via_cities.join(" · ")}</div>
              )}
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white"><X size={20}/></button>
          </div>
          <div className="mt-3 text-xs opacity-90 flex flex-wrap gap-x-3 gap-y-1">
            <span><Users size={12} className="inline -mt-0.5"/> {trip.driver_name}</span>
            {trip.driver_verified && (
              <span className="bg-emerald-500/40 rounded px-1.5 py-0.5 flex items-center gap-0.5" data-testid="rideshare-modal-verified"><ShieldCheck size={11}/> Verified driver</span>
            )}
            {trip.driver_avg_rating != null && (
              <span className="bg-amber-500/30 rounded px-1.5 py-0.5 flex items-center gap-0.5" data-testid="rideshare-modal-rating"><Star size={11} className="fill-amber-300"/> {trip.driver_avg_rating} · {trip.driver_ride_count || 0} ride{trip.driver_ride_count!==1?"s":""}</span>
            )}
            {trip.car_make && <span><Car size={12} className="inline -mt-0.5"/> {trip.car_make} {trip.car_model}</span>}
            {trip.boot_size && <span><Briefcase size={12} className="inline -mt-0.5"/> {trip.boot_size} boot</span>}
            {trip.women_only && <span className="bg-pink-500/30 rounded px-1.5 py-0.5">Women-only</span>}
            {trip.instant_book && <span className="bg-emerald-500/30 rounded px-1.5 py-0.5">Instant book</span>}
          </div>
        </div>

        <div className="p-5 space-y-3">
          {trip.notes && (
            <div className="text-xs text-stone-600 bg-stone-50 p-3 rounded-xl">{trip.notes}</div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase text-stone-500 font-black tracking-widest mb-1">Seats</div>
              <div className="flex gap-1">
                {[1,2,3,4].filter(n => n <= trip.seats_available).map(n =>
                  <button key={n} onClick={() => setSeats(n)}
                    className={`flex-1 rounded-lg h-9 text-sm font-bold ${seats === n ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-700"}`}
                    data-testid={`rideshare-seats-${n}`}>{n}</button>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-stone-500 font-black tracking-widest mb-1">Total</div>
              <div className="h-9 flex items-center px-3 rounded-lg bg-emerald-50 text-emerald-800 font-black">{fmtINR(seats * trip.price_per_seat_inr)}</div>
            </div>
          </div>
          <Input placeholder="Pickup point (optional)" value={pickup} onChange={e => setPickup(e.target.value)} data-testid="rideshare-book-pickup"/>
          <Input placeholder="Drop-off point (optional)" value={drop} onChange={e => setDrop(e.target.value)} data-testid="rideshare-book-drop"/>
          <Textarea placeholder="Note to driver (optional)" value={note} onChange={e => setNote(e.target.value)} rows={2} data-testid="rideshare-book-note"/>
          <Button onClick={book} disabled={busy || trip.seats_available === 0} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="rideshare-book-submit">
            {trip.instant_book ? "Book & pay" : "Request booking · Pay now"}
          </Button>
          <div className="text-[10px] text-stone-400 text-center leading-tight">
            Free cancel &gt; 24h · 50% refund 6-24h · No refund &lt; 6h before departure
          </div>
        </div>

        <div className="border-t p-5">
          <ReviewSection entityType="rideshare_trip" entityId={trip.trip_id} entityLabel="ride" />
        </div>
      </div>

      {fpOpen && booking && (
        <FindPayCheckout
          open={fpOpen}
          onOpenChange={setFpOpen}
          merchantId="fpm_platform"
          amountPaise={amountPaise}
          currency="INR"
          description={`Carpool · ${trip.from_city} → ${trip.to_city} · ${seats} seat${seats>1?"s":""}`}
          context={{ kind: "rideshare_book", booking_id: booking.booking_id, trip_id: trip.trip_id }}
          onSuccess={onPaid}
        />
      )}
      {justPaidBookingId && (
        <AutoShareOnPayModal trip={trip} onClose={() => { setJustPaidBookingId(null); onClose?.(); }}/>
      )}
    </div>
  );
}

// ─── Publish ─────────────────────────────────────────────────────────────
function PublishPanel({ user, onDone }) {
  const [form, setForm] = useState({
    from_city: "", to_city: "", via_cities: "",
    depart_at: "", seats_total: 3, price_per_seat_inr: 500,
    car_make: "", car_model: "", boot_size: "medium",
    women_only: false, instant_book: true, notes: "",
  });
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saveTpl, setSaveTpl] = useState(false);
  const [tplName, setTplName] = useState("");
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  useEffect(() => { if (user) api.get("/rideshare/templates").then(r => setTemplates(r.data.items || [])).catch(() => {}); }, [user]);
  const submit = async () => {
    if (!form.from_city || !form.to_city) return toast.error("From / To required");
    if (!form.depart_at) return toast.error("Pick a date & time");
    setBusy(true);
    const payload = {
      ...form,
      via_cities: form.via_cities ? form.via_cities.split(",").map(s => s.trim()).filter(Boolean) : [],
      depart_at: new Date(form.depart_at).toISOString(),
      seats_total: parseInt(form.seats_total, 10) || 1,
      price_per_seat_inr: parseInt(form.price_per_seat_inr, 10) || 100,
    };
    try {
      const { data } = await api.post("/rideshare/trips", payload);
      toast.success(`Published · ${data.from_city} → ${data.to_city}`);
      if (saveTpl && tplName.trim()) {
        await api.post("/rideshare/templates", {
          name: tplName.trim(), weekdays: [], time_hhmm: new Date(payload.depart_at).toISOString().slice(11, 16),
          trip: payload,
        });
        toast.success("Template saved");
      }
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to publish"); }
    finally { setBusy(false); }
  };
  const republish = async (t) => {
    const today = new Date().toISOString().slice(0, 10);
    try {
      await api.post(`/rideshare/templates/${t.template_id}/publish`, null, { params: { date: today } });
      toast.success(`Re-published '${t.name}' for today`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <DriverVerifyCard />
      {templates.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="rideshare-templates-panel">
          <div className="text-xs uppercase text-stone-500 font-black tracking-widest mb-2 flex items-center gap-2"><Repeat size={12}/> My templates</div>
          <div className="space-y-1">
            {templates.map(t => (
              <div key={t.template_id} className="flex items-center justify-between p-2 rounded-lg bg-stone-50 text-sm">
                <div>
                  <div className="font-semibold text-stone-800">{t.name}</div>
                  <div className="text-[11px] text-stone-500">{t.trip.from_city} → {t.trip.to_city} · {t.time_hhmm}</div>
                </div>
                <button onClick={() => republish(t)} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full px-3 py-1" data-testid={`rideshare-republish-${t.template_id}`}>Re-publish today</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="rideshare-publish-form">
        <div className="text-lg font-bold text-stone-800">Publish a trip</div>
        <div className="text-xs text-stone-500 mt-0.5">Riders pay upfront via FindPay — you receive funds when the ride is marked complete.</div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input placeholder="From city*" value={form.from_city} onChange={e => upd("from_city", e.target.value)} data-testid="rideshare-pub-from" />
          <Input placeholder="To city*"   value={form.to_city}   onChange={e => upd("to_city", e.target.value)}   data-testid="rideshare-pub-to" />
          <Input placeholder="Via cities (comma separated)" value={form.via_cities} onChange={e => upd("via_cities", e.target.value)} className="sm:col-span-2"/>
          <Input type="datetime-local" value={form.depart_at} onChange={e => upd("depart_at", e.target.value)} data-testid="rideshare-pub-depart" />
          <Input type="number" min="1" max="6" placeholder="Seats" value={form.seats_total} onChange={e => upd("seats_total", e.target.value)} data-testid="rideshare-pub-seats" />
          <Input type="number" min="1" placeholder="Price per seat (₹)" value={form.price_per_seat_inr} onChange={e => upd("price_per_seat_inr", e.target.value)} data-testid="rideshare-pub-price" />
          <select value={form.boot_size} onChange={e => upd("boot_size", e.target.value)} className="h-10 px-3 rounded-md border border-input text-sm">
            <option value="small">Small boot</option>
            <option value="medium">Medium boot</option>
            <option value="large">Large boot</option>
          </select>
          <Input placeholder="Car make (e.g., Maruti)"  value={form.car_make}  onChange={e => upd("car_make", e.target.value)} />
          <Input placeholder="Car model (e.g., Swift)" value={form.car_model} onChange={e => upd("car_model", e.target.value)} />
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-xs text-stone-600">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.instant_book} onChange={e => upd("instant_book", e.target.checked)}/> Instant book</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.women_only}   onChange={e => upd("women_only", e.target.checked)}/> Women-only trip</label>
        </div>
        <Textarea placeholder="Notes (AC on, no smoking, chai stop, etc.)" value={form.notes} onChange={e => upd("notes", e.target.value)} rows={2} className="mt-2"/>

        <div className="mt-3 p-3 rounded-xl bg-stone-50 border border-stone-200">
          <label className="flex items-center gap-2 text-xs text-stone-700">
            <input type="checkbox" checked={saveTpl} onChange={e => setSaveTpl(e.target.checked)}/>
            Save as a recurring template
          </label>
          {saveTpl && (
            <Input placeholder="Template name (e.g., Weekday DEL → GGN)" value={tplName} onChange={e => setTplName(e.target.value)} className="mt-2 h-9"/>
          )}
        </div>

        <Button onClick={submit} disabled={busy} className="mt-3 w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="rideshare-pub-submit">
          {busy ? "Publishing…" : "Publish trip"}
        </Button>
      </div>
    </div>
  );
}

// ─── Mine ────────────────────────────────────────────────────────────────
function MinePanel({ user }) {
  const [role, setRole]  = useState("passenger");
  const [data, setData]  = useState(null);
  const load = () => user && api.get("/rideshare/mine", { params: { role }}).then(r => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role, user]);
  if (!user) return <div className="text-center text-stone-400 py-8 text-sm">Sign in to see your trips.</div>;
  return (
    <div>
      <div className="grid grid-cols-2 gap-1 bg-white rounded-2xl p-1 shadow-sm mb-3">
        {["passenger", "driver"].map(r => (
          <button key={r} onClick={() => setRole(r)}
            className={`h-9 rounded-xl text-xs font-bold uppercase tracking-widest ${role === r ? "bg-emerald-600 text-white" : "text-stone-500 hover:bg-stone-100"}`}
            data-testid={`rideshare-mine-${r}`}>
            {r === "passenger" ? "As Passenger" : "As Driver"}
          </button>
        ))}
      </div>
      {role === "passenger" && <PassengerList bookings={data?.bookings} onChange={load} />}
      {role === "driver"    && <DriverList    trips={data?.trips} pending={data?.pending_approvals} onChange={load}/>}
    </div>
  );
}

function PassengerList({ bookings, onChange }) {
  const list = bookings || [];
  const [reviewFor, setReviewFor] = useState(null);   // booking obj currently being rated
  const cancel = async (b) => {
    if (!window.confirm("Cancel this booking? Refund depends on how close to departure.")) return;
    try {
      const { data } = await api.post(`/rideshare/bookings/${b.booking_id}/cancel`);
      toast.success(`Cancelled — refund ${data.refund_pct}%`);
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  const openChat = async (b) => {
    try {
      const { data } = await api.get(`/rideshare/bookings/${b.booking_id}/chat`);
      window.location.href = `/messenger?conv=${data.conv_id}`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Chat unavailable"); }
  };
  // Auto-pop the review sheet the FIRST time a completed booking appears
  // in the list (per-device idempotent via localStorage).
  useEffect(() => {
    const firstUnrated = list.find(b =>
      b.status === "completed" && !localStorage.getItem(`rs-rated-${b.booking_id}`),
    );
    if (firstUnrated && !reviewFor) setReviewFor(firstUnrated);
  }, [list]); // eslint-disable-line
  if (list.length === 0) return <div className="bg-white rounded-2xl p-6 text-center text-stone-400 text-sm">No bookings yet.</div>;
  // Sticky auto-share banner for the most-recently paid booking that has
  // no share token acknowledged yet (per-device via localStorage).
  const bannerFor = list.find(b =>
    (b.status === "paid" || b.status === "paid_pending_approval") &&
    !localStorage.getItem(`rs-shared-banner-${b.booking_id}`),
  );
  return (
    <>
      <EmergencyContactsCard/>
      {bannerFor && (
        <AutoShareStickyBanner booking={bannerFor}
                                onDismiss={() => localStorage.setItem(`rs-shared-banner-${bannerFor.booking_id}`, "1")}/>
      )}
      <div className="space-y-2">
        {list.map(b => {
          const rated = !!localStorage.getItem(`rs-rated-${b.booking_id}`);
          return (
          <div key={b.booking_id} className="bg-white rounded-2xl p-3 shadow-sm" data-testid={`rideshare-mine-b-${b.booking_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-stone-500">Trip #{b.trip_id.slice(3,10)}</div>
                <div className="font-bold">{b.seats} seat{b.seats>1?"s":""} · {fmtINR(b.amount_inr)}</div>
              </div>
              <StatusPill s={b.status} />
            </div>
            <div className="mt-1 flex gap-3 flex-wrap items-center">
              {["approved","requested","paid","paid_pending_approval"].includes(b.status) && (
                <button onClick={() => cancel(b)} className="text-xs text-red-500 underline" data-testid={`rideshare-cancel-${b.booking_id}`}>Cancel</button>
              )}
              {["paid","paid_pending_approval","completed"].includes(b.status) && (
                <button onClick={() => openChat(b)} className="text-xs text-emerald-700 font-bold underline" data-testid={`rideshare-chat-${b.booking_id}`}>Chat driver</button>
              )}
              {["paid","paid_pending_approval"].includes(b.status) && (
                <PassengerTrackerButton tripId={b.trip_id} bookingId={b.booking_id} />
              )}
              {b.status === "completed" && (
                <>
                  <PlaybackButton tripId={b.trip_id} />
                  <ShareRideButton tripId={b.trip_id} />
                  {!rated && (
                    <button onClick={() => setReviewFor(b)}
                            className="text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-full px-3 py-1 font-bold inline-flex items-center gap-1"
                            data-testid={`rideshare-rate-${b.booking_id}`}>
                      <Star size={12}/> Rate driver
                    </button>
                  )}
                  {rated && (
                    <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={11}/> Thanks — rated</span>
                  )}
                </>
              )}
              {["paid","paid_pending_approval"].includes(b.status) && (
                <ShareRideButton tripId={b.trip_id} />
              )}
            </div>
          </div>
          );
        })}
      </div>
      {reviewFor && (
        <RateDriverSheet
          booking={reviewFor}
          onClose={() => setReviewFor(null)}
          onDone={() => { localStorage.setItem(`rs-rated-${reviewFor.booking_id}`, "1"); setReviewFor(null); onChange?.(); }}
        />
      )}
    </>
  );
}

function DriverList({ trips, pending, onChange }) {
  const [rateQueue, setRateQueue] = useState([]);  // list of {trip_id, passenger_id, passenger_name}
  const approve = async (b) => {
    try { await api.post(`/rideshare/bookings/${b.booking_id}/approve`); toast.success("Approved"); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const openChat = async (b) => {
    try {
      const { data } = await api.get(`/rideshare/bookings/${b.booking_id}/chat`);
      window.location.href = `/messenger?conv=${data.conv_id}`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Chat unavailable"); }
  };
  const startTrip = async (t) => {
    try {
      await api.post(`/rideshare/trips/${t.trip_id}/start`);
      toast.success("Trip started · broadcasting live location");
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Start failed"); }
  };
  const complete = async (b) => {
    try { await api.post(`/rideshare/bookings/${b.booking_id}/complete`); toast.success("Marked complete — payment released"); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Complete failed"); }
  };
  const cancelTrip = async (t) => {
    if (!window.confirm("Cancel the entire trip? All bookings will be flagged for full refund.")) return;
    try { await api.post(`/rideshare/trips/${t.trip_id}/cancel`); toast.success("Trip cancelled"); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  // Queue up the driver-side "Rate passenger" sheets — one per booking that
  // hasn't been rated yet on this device.
  const queueRatingsForTrip = async (tripId) => {
    try {
      const { data } = await api.get(`/rideshare/mine`, { params: { role: "driver" }});
      // Fallback: fetch bookings via the trip's bookings endpoint would need
      // extra plumbing; simpler — pull from pending + local knowledge.
      // Use direct DB-lookup via /rideshare/trips/{id} isn't enough. Instead
      // we call a lightweight helper: reuse existing /mine plus the passenger
      // list from the same tripId (via a public field). Best-effort — if we
      // don't get names, we still show the passenger_id.
      const bookingsCur = await api.get(`/rideshare/trips/${tripId}/passengers`).catch(() => ({ data: { items: [] }}));
      const rows = (bookingsCur.data?.items || []).filter(b =>
        !localStorage.getItem(`rs-rated-passenger-${b.booking_id}`)
      );
      if (rows.length) setRateQueue(q => [...q, ...rows.map(r => ({ ...r, trip_id: tripId }))]);
    } catch { /* silent */ }
  };
  const arrive = async (t) => {
    if (!window.confirm("Mark this trip as arrived? All paid bookings will be completed and payments released to you.")) return;
    try {
      const { data } = await api.post(`/rideshare/trips/${t.trip_id}/arrive`);
      toast.success(`Arrived · ${data.settled_bookings} booking${data.settled_bookings !== 1 ? "s" : ""} settled`);
      onChange?.();
      queueRatingsForTrip(t.trip_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Arrival failed"); }
  };
  const dequeue = () => setRateQueue(q => q.slice(1));
  return (
    <>
      {pending && pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-3">
          <div className="text-xs uppercase text-amber-700 font-black tracking-widest mb-2">Pending approvals</div>
          {pending.map(b => (
            <div key={b.booking_id} className="flex items-center justify-between text-sm py-1.5">
              <div>
                <div className="font-bold">{b.passenger_name} · {b.seats} seat{b.seats>1?"s":""}</div>
                <div className="text-[11px] text-stone-500">Trip #{b.trip_id.slice(3,10)} · {fmtINR(b.amount_inr)}</div>
              </div>
              <div className="flex gap-2 items-center">
                {["paid","paid_pending_approval","completed"].includes(b.status) && (
                  <button onClick={() => openChat(b)} className="text-xs text-emerald-700 font-bold underline" data-testid={`rideshare-driverchat-${b.booking_id}`}>Chat</button>
                )}
                <button onClick={() => approve(b)} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-3 py-1 font-bold" data-testid={`rideshare-approve-${b.booking_id}`}>Approve</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2">
        {(trips || []).map(t => (
          <div key={t.trip_id} className="bg-white rounded-2xl p-3 shadow-sm" data-testid={`rideshare-mine-t-${t.trip_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-stone-500">{fmtDate(t.depart_at)}</div>
                <div className="font-bold">{t.from_city} → {t.to_city}</div>
                <div className="text-[11px] text-stone-500">{t.seats_available}/{t.seats_total} seats · {fmtINR(t.price_per_seat_inr)}/seat</div>
              </div>
              <StatusPill s={t.status} />
            </div>
            <div className="mt-1 flex gap-3 items-center">
              {t.status === "published" && (
                <>
                  <button onClick={() => cancelTrip(t)} className="text-xs text-red-500 underline" data-testid={`rideshare-cancel-trip-${t.trip_id}`}>Cancel trip</button>
                  <button onClick={() => startTrip(t)} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-3 py-1 font-bold" data-testid={`rideshare-start-${t.trip_id}`}>I've started · Share location</button>
                </>
              )}
              {t.status === "started" && (
                <>
                  <DriverLiveBroadcaster tripId={t.trip_id} />
                  <button onClick={() => arrive(t)} className="text-xs bg-rose-600 hover:bg-rose-700 text-white rounded-full px-3 py-1 font-bold inline-flex items-center gap-1" data-testid={`rideshare-arrive-${t.trip_id}`}>
                    <Flag size={12}/> I've arrived
                  </button>
                  {t.women_only && (
                    <ConvoyButton trip={t}/>
                  )}
                </>
              )}
              {t.status === "completed" && (
                <>
                  <PlaybackButton tripId={t.trip_id} />
                  <button onClick={() => queueRatingsForTrip(t.trip_id)}
                          className="text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-full px-3 py-1 font-bold inline-flex items-center gap-1"
                          data-testid={`rideshare-rate-passengers-${t.trip_id}`}>
                    <Star size={12}/> Rate passengers
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {(!trips || trips.length === 0) && (
          <div className="bg-white rounded-2xl p-6 text-center text-stone-400 text-sm">No trips yet — publish one from the Publish tab.</div>
        )}
      </div>
      {rateQueue.length > 0 && (
        <RatePassengerSheet
          entry={rateQueue[0]}
          onClose={dequeue}
          onDone={() => { localStorage.setItem(`rs-rated-passenger-${rateQueue[0].booking_id}`, "1"); dequeue(); }}
        />
      )}
    </>
  );
}

// ─── Driver verification card (shown on Publish tab) ────────────────────
function DriverVerifyCard() {
  const { user } = useAuth();
  const [prof, setProf] = useState(null);
  const [dl, setDl]     = useState("");
  const [rc, setRc]     = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => user && api.get(`/rideshare/driver/${user.user_id}`).then(r => setProf(r.data)).catch(() => setProf(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);
  if (!user) return null;
  const submit = async () => {
    if (!dl.trim()) return toast.error("DL number required");
    setBusy(true);
    try {
      await api.post("/rideshare/driver/verify", { dl_number: dl.trim(), rc_number: rc.trim() || null });
      toast.success("Verified — the badge is now on all your published trips.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };
  if (prof?.verified) {
    return (
      <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between" data-testid="rideshare-driver-verified-card">
        <div className="flex items-center gap-2">
          <ShieldCheck size={22} className="text-emerald-600" />
          <div>
            <div className="font-bold text-stone-800 flex items-center gap-1.5">Verified driver
              {prof.avg_rating != null && (
                <span className="text-xs text-amber-700 flex items-center gap-0.5"><Star size={11} className="fill-amber-500 text-amber-500"/> {prof.avg_rating}</span>
              )}
            </div>
            <div className="text-[11px] text-stone-500">{prof.ride_count} completed ride{prof.ride_count !== 1 ? "s" : ""} · {prof.review_count} review{prof.review_count !== 1 ? "s" : ""}</div>
          </div>
        </div>
        <span className="text-[10px] text-stone-400">Self-attested</span>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="rideshare-driver-verify-card">
      <div className="flex items-center gap-2 text-emerald-700 font-semibold">
        <ShieldCheck size={20}/> Get the "Verified driver" badge
      </div>
      <div className="text-xs text-stone-500 mt-1">Passengers pick trips from verified drivers 3× more often. Self-attested for now; DigiLocker verification coming soon.</div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Input placeholder="Driving Licence number*" value={dl} onChange={e => setDl(e.target.value)} data-testid="rideshare-verify-dl"/>
        <Input placeholder="Vehicle RC (optional)" value={rc} onChange={e => setRc(e.target.value)} data-testid="rideshare-verify-rc"/>
      </div>
      <Button onClick={submit} disabled={busy} className="mt-3 w-full h-10 bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="rideshare-verify-submit">
        {busy ? "Verifying…" : "Verify me"}
      </Button>
    </div>
  );
}


// ─── Live tracking components ────────────────────────────────────────────
function PassengerTrackerButton({ tripId, bookingId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-xs text-sky-700 font-bold underline" data-testid={`rideshare-track-${bookingId}`}>Track live</button>
      {open && <PassengerTrackerModal tripId={tripId} onClose={() => setOpen(false)} />}
    </>
  );
}

function PassengerTrackerModal({ tripId, onClose }) {
  const [loc, setLoc] = useState(null);
  const [route, setRoute] = useState(null);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);
  const mapRef  = useRef(null);
  const driverMarkerRef = useRef(null);

  // Poll live location every 15s.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/rideshare/trips/${tripId}/location`);
        if (!cancelled) setLoc(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Cannot fetch location");
      }
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [tripId]);

  // Fetch route polyline once we have coords (re-fetch every 60s so it
  // follows the driver's progress).
  useEffect(() => {
    let cancelled = false;
    const fetchRoute = async () => {
      try {
        const { data } = await api.get(`/rideshare/trips/${tripId}/route`);
        if (!cancelled) setRoute(data);
      } catch { /* silent */ }
    };
    fetchRoute();
    const id = setInterval(fetchRoute, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [tripId]);

  // Initialise the MapLibre map once we have anchor coords.
  useEffect(() => {
    if (!wrapRef.current || mapRef.current) return;
    if (!loc || (loc.from_lat == null && loc.lat == null)) return;
    const center = loc.lat != null
      ? [loc.lng, loc.lat]
      : [loc.from_lng, loc.from_lat];
    const map = new maplibregl.Map({
      container: wrapRef.current,
      style: "https://tiles.openfreemap.org/styles/liberty",
      center, zoom: 8, attributionControl: false,
    });
    map.on("error", (e) => {
      if (String(e?.error?.message || "").includes("tiles")) {
        try { map.setStyle("https://demotiles.maplibre.org/style.json"); } catch { /* ignore */ }
      }
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    // Pickup + drop markers.
    if (loc.from_lat != null && loc.from_lng != null) {
      new maplibregl.Marker({ color: "#059669" })
        .setLngLat([loc.from_lng, loc.from_lat])
        .setPopup(new maplibregl.Popup().setText("Pickup"))
        .addTo(map);
    }
    if (loc.to_lat != null && loc.to_lng != null) {
      new maplibregl.Marker({ color: "#dc2626" })
        .setLngLat([loc.to_lng, loc.to_lat])
        .setPopup(new maplibregl.Popup().setText("Drop-off"))
        .addTo(map);
    }
    // Driver as a custom emoji car icon.
    if (loc.lat != null && loc.lng != null) {
      const el = document.createElement("div");
      el.setAttribute("data-testid", "rideshare-driver-car-marker");
      el.style.cssText = "font-size:26px;transform:translateY(-4px);text-shadow:0 2px 4px rgba(0,0,0,0.4);";
      el.textContent = "🚗";
      driverMarkerRef.current = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([loc.lng, loc.lat])
        .addTo(map);
    }
    mapRef.current = map;
    return () => { try { map.remove(); } catch { /* ignore */ } mapRef.current = null; };
  }, [loc]);

  // Update driver marker + route polyline as new data lands.
  useEffect(() => {
    if (!mapRef.current || !loc || loc.lat == null) return;
    if (driverMarkerRef.current) {
      driverMarkerRef.current.setLngLat([loc.lng, loc.lat]);
    } else {
      const el = document.createElement("div");
      el.setAttribute("data-testid", "rideshare-driver-car-marker");
      el.style.cssText = "font-size:26px;transform:translateY(-4px);text-shadow:0 2px 4px rgba(0,0,0,0.4);";
      el.textContent = "🚗";
      driverMarkerRef.current = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([loc.lng, loc.lat])
        .addTo(mapRef.current);
    }
  }, [loc?.lat, loc?.lng]);

  useEffect(() => {
    if (!mapRef.current || !route?.coordinates?.length) return;
    const map = mapRef.current;
    const applyLayer = () => {
      const geo = { type: "Feature", geometry: { type: "LineString", coordinates: route.coordinates } };
      if (map.getSource("rs-route")) {
        map.getSource("rs-route").setData(geo);
      } else {
        map.addSource("rs-route", { type: "geojson", data: geo });
        map.addLayer({
          id: "rs-route-line", type: "line", source: "rs-route",
          paint: { "line-color": "#059669", "line-width": 4, "line-opacity": 0.85 },
          layout: { "line-cap": "round", "line-join": "round" },
        });
      }
      // Fit bounds to the whole polyline first time.
      try {
        const b = new maplibregl.LngLatBounds();
        route.coordinates.forEach(c => b.extend(c));
        map.fitBounds(b, { padding: 40, duration: 600, maxZoom: 12 });
      } catch { /* ignore */ }
    };
    if (map.isStyleLoaded()) applyLayer(); else map.once("load", applyLayer);
  }, [route]);

  const pct = loc?.progress_pct;
  const eta = loc?.eta_minutes;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="rideshare-tracker-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-bold text-stone-800">Live ride tracking</div>
          <div className="flex items-center gap-2">
            <SosButton tripId={tripId} currentLoc={loc}/>
            <button onClick={onClose} className="text-stone-400 hover:text-stone-600" data-testid="rideshare-tracker-close"><X size={18}/></button>
          </div>
        </div>
        {error && <div className="p-4 text-sm text-red-600" data-testid="rideshare-tracker-error">{error}</div>}
        {!error && (
          <>
            <div ref={wrapRef} style={{ width: "100%", height: 320 }} data-testid="rideshare-tracker-map" />
            <div className="px-4 py-3 space-y-2">
              {loc ? (
                <>
                  <div className="flex items-center justify-between">
                    <StatusPill s={loc.status}/>
                    {eta != null && (
                      <div className="text-sm font-bold text-emerald-700" data-testid="rideshare-tracker-eta">
                        ETA · {Math.round(eta)} min
                      </div>
                    )}
                  </div>
                  {pct != null && (
                    <div data-testid="rideshare-tracker-progress">
                      <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="flex justify-between text-[11px] text-stone-500 mt-1">
                        <span>{Math.round(pct)}% travelled</span>
                        {loc.remaining_km != null && <span>{loc.remaining_km} km left</span>}
                      </div>
                    </div>
                  )}
                  {loc.near_dropoff && (
                    <div className="text-xs text-emerald-700 font-bold flex items-center gap-1" data-testid="rideshare-tracker-near">
                      <CheckCircle2 size={14}/> Driver has almost arrived — please be ready at the drop-off point.
                    </div>
                  )}
                  {loc.speed_kmh != null && loc.lat != null && (
                    <div className="text-[11px] text-stone-500">
                      {loc.speed_kmh.toFixed(0)} km/h · updated {loc.updated_at ? new Date(loc.updated_at).toLocaleTimeString() : "—"}
                    </div>
                  )}
                  {loc.lat != null && (
                    <a href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} target="_blank" rel="noreferrer"
                       className="inline-block text-sm text-emerald-700 font-bold underline"
                       data-testid="rideshare-track-open-maps">Open in Google Maps →</a>
                  )}
                  {loc.lat == null && <div className="text-sm text-stone-500">Driver hasn't started broadcasting yet.</div>}
                </>
              ) : (
                <div className="text-sm text-stone-400">Waiting for driver's location…</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function DriverLiveBroadcaster({ tripId }) {
  const [broadcasting, setBroadcasting] = useState(false);
  const [lastLoc, setLastLoc] = useState(null);
  useEffect(() => {
    if (!broadcasting) return;
    if (!navigator.geolocation) { toast.error("GPS not available on this device"); setBroadcasting(false); return; }
    const push = () => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const p = { lat: pos.coords.latitude, lng: pos.coords.longitude, speed_kmh: (pos.coords.speed || 0) * 3.6 };
          try {
            await api.post(`/rideshare/trips/${tripId}/location`, p);
            setLastLoc({ ...p, at: new Date().toLocaleTimeString() });
          } catch (e) { /* silent */ }
        },
        (err) => { toast.error("GPS: " + err.message); },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 },
      );
    };
    push();
    const id = setInterval(push, 30000);
    return () => clearInterval(id);
  }, [broadcasting, tripId]);
  if (!broadcasting) {
    return <button onClick={() => setBroadcasting(true)} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-3 py-1 font-bold" data-testid={`rideshare-broadcast-start-${tripId}`}>Start broadcasting</button>;
  }
  return (
    <div className="text-[11px] text-emerald-700 flex items-center gap-2" data-testid={`rideshare-broadcasting-${tripId}`}>
      <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>
      Live · every 30s
      {lastLoc && <span className="text-stone-500">· last: {lastLoc.at}</span>}
      <button onClick={() => setBroadcasting(false)} className="text-red-500 underline">Stop</button>
    </div>
  );
}


// ─── Ride Playback (breadcrumb replay) ──────────────────────────────────
function PlaybackButton({ tripId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-xs text-sky-700 font-bold underline" data-testid={`rideshare-playback-${tripId}`}>Replay ride</button>
      {open && <PlaybackModal tripId={tripId} onClose={() => setOpen(false)} />}
    </>
  );
}

function PlaybackModal({ tripId, onClose }) {
  const [data, setData]   = useState(null);
  const [idx, setIdx]     = useState(0);
  const [playing, setPly] = useState(false);
  const wrapRef = useRef(null);
  const mapRef  = useRef(null);
  const carRef  = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/rideshare/trips/${tripId}/playback`);
        if (!cancelled) setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Playback unavailable");
      }
    })();
    return () => { cancelled = true; };
  }, [tripId]);

  useEffect(() => {
    if (!data || !wrapRef.current || mapRef.current) return;
    if (!data.trail?.length && data.from_lat == null) return;
    const first = data.trail?.[0] || { lat: data.from_lat, lng: data.from_lng };
    const map = new maplibregl.Map({
      container: wrapRef.current,
      style: "https://tiles.openfreemap.org/styles/liberty",
      center: [first.lng, first.lat], zoom: 8, attributionControl: false,
    });
    map.on("error", (e) => {
      if (String(e?.error?.message || "").includes("tiles")) {
        try { map.setStyle("https://demotiles.maplibre.org/style.json"); } catch { /* ignore */ }
      }
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    // Anchor markers.
    if (data.from_lat != null && data.from_lng != null) {
      new maplibregl.Marker({ color: "#059669" }).setLngLat([data.from_lng, data.from_lat]).addTo(map);
    }
    if (data.to_lat != null && data.to_lng != null) {
      new maplibregl.Marker({ color: "#dc2626" }).setLngLat([data.to_lng, data.to_lat]).addTo(map);
    }
    // Full breadcrumb path.
    if (data.trail?.length) {
      const coords = data.trail.map(p => [p.lng, p.lat]);
      const drawPath = () => {
        if (map.getSource("rs-trail")) return;
        map.addSource("rs-trail", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: coords }}});
        map.addLayer({ id: "rs-trail-line", type: "line", source: "rs-trail",
          paint: { "line-color": "#0369a1", "line-width": 4, "line-opacity": 0.85 },
          layout: { "line-cap": "round", "line-join": "round" }});
        try {
          const b = new maplibregl.LngLatBounds();
          coords.forEach(c => b.extend(c));
          if (data.from_lat != null) b.extend([data.from_lng, data.from_lat]);
          if (data.to_lat != null)   b.extend([data.to_lng,   data.to_lat]);
          map.fitBounds(b, { padding: 40, duration: 400, maxZoom: 12 });
        } catch { /* ignore */ }
      };
      if (map.isStyleLoaded()) drawPath(); else map.once("load", drawPath);
      // Car marker at index 0.
      const el = document.createElement("div");
      el.setAttribute("data-testid", "rideshare-playback-car");
      el.style.cssText = "font-size:26px;text-shadow:0 2px 4px rgba(0,0,0,0.4);";
      el.textContent = "🚗";
      carRef.current = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(coords[0]).addTo(map);
    }
    mapRef.current = map;
    return () => { try { map.remove(); } catch { /* ignore */ } mapRef.current = null; };
  }, [data]);

  // Move car marker whenever the slider index changes.
  useEffect(() => {
    if (!data?.trail?.length || !carRef.current) return;
    const p = data.trail[Math.max(0, Math.min(idx, data.trail.length - 1))];
    if (p) carRef.current.setLngLat([p.lng, p.lat]);
  }, [idx, data]);

  // Auto-play → advance the slider 1 step every 500ms.
  useEffect(() => {
    if (!playing || !data?.trail?.length) return;
    const id = setInterval(() => {
      setIdx(i => {
        if (i >= data.trail.length - 1) { setPly(false); return i; }
        return i + 1;
      });
    }, 500);
    return () => clearInterval(id);
  }, [playing, data]);

  const total = data?.trail?.length || 0;
  const at = total ? data.trail[Math.min(idx, total - 1)]?.at : null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="rideshare-playback-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-bold text-stone-800">Replay ride</div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600" data-testid="rideshare-playback-close"><X size={18}/></button>
        </div>
        <div ref={wrapRef} style={{ width: "100%", height: 320 }} data-testid="rideshare-playback-map"/>
        <div className="px-4 py-3 space-y-2">
          {!data && <div className="text-sm text-stone-400">Loading trail…</div>}
          {data && total === 0 && (
            <div className="text-sm text-stone-500">No GPS pings recorded for this trip.</div>
          )}
          {data && total > 0 && (
            <>
              <div className="flex items-center gap-3">
                <button onClick={() => setPly(p => !p)}
                        className="text-xs bg-sky-600 hover:bg-sky-700 text-white rounded-full px-3 py-1 font-bold"
                        data-testid="rideshare-playback-play">
                  {playing ? "Pause" : "Play"}
                </button>
                <input type="range" min={0} max={total - 1} value={idx}
                       onChange={e => { setIdx(parseInt(e.target.value, 10)); setPly(false); }}
                       className="flex-1 accent-sky-600" data-testid="rideshare-playback-slider"/>
                <span className="text-[11px] text-stone-500 tabular-nums">{idx + 1}/{total}</span>
              </div>
              {at && <div className="text-[11px] text-stone-500">At: {new Date(at).toLocaleString()}</div>}
              <div className="text-[11px] text-stone-400">Breadcrumbs are kept for 30 days after the ride.</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Rate Driver sheet (auto-pop on first-view of completed booking) ────
function RateDriverSheet({ booking, onClose, onDone }) {
  const [stars, setStars] = useState(5);
  const [body, setBody]   = useState("");
  const [busy, setBusy]   = useState(false);
  const submit = async () => {
    if (stars < 1) return;
    setBusy(true);
    try {
      await api.post("/reviews/universal", {
        entity_type: "rideshare_trip", entity_id: booking.trip_id,
        rating: stars, body: body.trim(), photos: [],
      });
      toast.success("Thanks — your rating is live");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rating");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="rideshare-rate-sheet">
      <div className="bg-white rounded-2xl max-w-sm w-full p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <div className="font-bold text-stone-800">Rate your driver</div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600" data-testid="rideshare-rate-close"><X size={18}/></button>
        </div>
        <div className="text-xs text-stone-500 mb-3">How was your ride on trip #{booking.trip_id.slice(3, 10)}?</div>
        <div className="flex items-center gap-1 mb-3">
          {[1,2,3,4,5].map(n => (
            <button key={n} onClick={() => setStars(n)} data-testid={`rideshare-rate-star-${n}`}>
              <Star size={28} className={n <= stars ? "text-amber-500 fill-amber-500" : "text-stone-200 fill-stone-200"}/>
            </button>
          ))}
        </div>
        <Textarea rows={3} placeholder="Tell others how the ride went (optional)…"
                  value={body} onChange={e => setBody(e.target.value)} data-testid="rideshare-rate-body"/>
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs text-stone-500 underline" data-testid="rideshare-rate-later">Later</button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="rideshare-rate-submit">
            {busy ? "Saving…" : "Submit rating"}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─── Share this ride (proof-of-safety link) ─────────────────────────────
function ShareRideButton({ tripId }) {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rideshare/trips/${tripId}/share`);
      const url = `${window.location.origin}${data.path}`;
      const text = `Following my ride live on Find2Near — ${url}`;
      let handled = false;
      if (navigator.share) {
        try { await navigator.share({ title: "My ride", text, url }); handled = true; } catch { /* user cancelled */ }
      }
      if (!handled && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied — send it to family.");
      } else if (!handled) {
        window.prompt("Copy this link:", url);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create share link"); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={share} disabled={busy}
            className="text-xs text-emerald-700 font-bold underline disabled:opacity-60 inline-flex items-center gap-1"
            data-testid={`rideshare-share-${tripId}`}>
      <Sparkles size={12}/> {busy ? "…" : "Share this ride"}
    </button>
  );
}


// ─── Rate Passenger sheet (driver-side reciprocity) ─────────────────────
function RatePassengerSheet({ entry, onClose, onDone }) {
  const [stars, setStars] = useState(5);
  const [body, setBody]   = useState("");
  const [busy, setBusy]   = useState(false);
  const submit = async () => {
    if (stars < 1) return;
    setBusy(true);
    try {
      await api.post("/reviews/universal", {
        entity_type: "passenger", entity_id: entry.passenger_id,
        rating: stars, body: body.trim(), photos: [],
      });
      toast.success("Thanks — rating saved");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rating");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="rideshare-rate-pax-sheet">
      <div className="bg-white rounded-2xl max-w-sm w-full p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <div className="font-bold text-stone-800">Rate your passenger</div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600" data-testid="rideshare-rate-pax-close"><X size={18}/></button>
        </div>
        <div className="text-xs text-stone-500 mb-3">How was <b>{entry.passenger_name || "your passenger"}</b>?</div>
        <div className="flex items-center gap-1 mb-3">
          {[1,2,3,4,5].map(n => (
            <button key={n} onClick={() => setStars(n)} data-testid={`rideshare-rate-pax-star-${n}`}>
              <Star size={28} className={n <= stars ? "text-amber-500 fill-amber-500" : "text-stone-200 fill-stone-200"}/>
            </button>
          ))}
        </div>
        <Textarea rows={3} placeholder="Punctual, polite, easy to reach…"
                  value={body} onChange={e => setBody(e.target.value)} data-testid="rideshare-rate-pax-body"/>
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs text-stone-500 underline" data-testid="rideshare-rate-pax-later">Skip</button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="rideshare-rate-pax-submit">
            {busy ? "Saving…" : "Submit rating"}
          </Button>
        </div>
      </div>
    </div>
  );
}



// ─── Women-Only Convoy (Slice 9) ────────────────────────────────────────
function ConvoyButton({ trip }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)}
              className="text-xs bg-purple-600 hover:bg-purple-700 text-white rounded-full px-3 py-1 font-bold inline-flex items-center gap-1"
              data-testid={`rideshare-convoy-btn-${trip.trip_id}`}>
        <Users size={12}/> Ride in convoy
      </button>
      {open && <ConvoyModal trip={trip} onClose={() => setOpen(false)}/>}
    </>
  );
}

function ConvoyModal({ trip, onClose }) {
  const [items, setItems]     = useState(null);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(false);
  const [radius, setRadius]   = useState(15);
  const [includePax, setIP]   = useState(true);
  const load = async (km = radius) => {
    setItems(null); setError(null);
    try {
      const { data } = await api.get(`/rideshare/convoy-candidates`,
                                       { params: { trip_id: trip.trip_id, radius_km: km }});
      setItems(data.items || []);
    } catch (e) { setError(e?.response?.data?.detail || "Cannot find candidates"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [trip.trip_id]);
  const form = async (other) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rideshare/trips/${trip.trip_id}/convoy`, {
        other_trip_id: other.trip_id, include_passengers: includePax,
      });
      toast.success("Convoy formed — opening group chat");
      window.location.href = `/messenger?conv=${data.conv_id}`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not form convoy"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[75] bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="rideshare-convoy-modal">
      <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <div className="font-bold text-stone-800 flex items-center gap-2"><Users size={16}/> Women-only convoys</div>
            <div className="text-[11px] text-stone-500">Ride together with a nearby women-only trip to {trip.to_city}.</div>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600" data-testid="rideshare-convoy-close"><X size={18}/></button>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1">Radius
              <select value={radius} onChange={e => { const n = parseInt(e.target.value, 10); setRadius(n); load(n); }}
                      className="border rounded px-1 py-0.5" data-testid="rideshare-convoy-radius">
                {[5,15,25,50].map(k => <option key={k} value={k}>{k} km</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={includePax} onChange={e => setIP(e.target.checked)}
                     data-testid="rideshare-convoy-include-pax"/>
              Include my passengers in the group chat
            </label>
          </div>
          {error && <div className="text-sm text-red-600" data-testid="rideshare-convoy-error">{error}</div>}
          {!error && items === null && <div className="text-sm text-stone-400">Searching for nearby women-only rides…</div>}
          {!error && items !== null && items.length === 0 && (
            <div className="text-sm text-stone-500">No women-only rides nearby. We'll surface them here the moment another driver starts one within {radius} km heading to {trip.to_city}.</div>
          )}
          {!error && items && items.map(c => (
            <div key={c.trip_id} className="border rounded-2xl px-3 py-2 flex items-center justify-between" data-testid={`rideshare-convoy-item-${c.trip_id}`}>
              <div>
                <div className="font-bold text-stone-800 flex items-center gap-1">{c.driver_name}
                  {c.driver_verified && <ShieldCheck size={12} className="text-emerald-600"/>}
                  {c.driver_avg_rating != null && (
                    <span className="text-[11px] text-amber-700 flex items-center gap-0.5 ml-1">
                      <Star size={10} className="fill-amber-500 text-amber-500"/> {c.driver_avg_rating}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-stone-500">{c.from_city} → {c.to_city} · {c.distance_km} km away</div>
              </div>
              <Button onClick={() => form(c)} disabled={busy}
                      className="bg-purple-600 hover:bg-purple-700 font-bold h-8 text-xs"
                      data-testid={`rideshare-convoy-form-${c.trip_id}`}>
                {busy ? "…" : "Form convoy"}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── SOS + Emergency Contacts + Auto-Share ──────────────────────────────
function SosButton({ tripId, currentLoc }) {
  const [busy, setBusy] = useState(false);
  const trigger = async () => {
    if (!window.confirm("Send SOS to your saved emergency contacts?")) return;
    setBusy(true);
    // Get browser GPS so the message carries a live pin (with a fallback).
    const gps = await new Promise((res) => {
      if (!navigator.geolocation) { res({}); return; }
      navigator.geolocation.getCurrentPosition(
        p => res({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy }),
        () => res({}),
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 2000 },
      );
    });
    // Fall back to the driver's last-known coords from the tracker if
    // browser GPS was denied (still useful for family).
    const lat = gps.lat ?? currentLoc?.lat;
    const lng = gps.lng ?? currentLoc?.lng;
    try {
      const { data } = await api.post(`/rideshare/trips/${tripId}/sos`,
                                        { lat, lng, accuracy_m: gps.accuracy_m });
      if (!data.contacts?.length) {
        toast.error("No emergency contacts saved — add one in Mine tab first.");
        return;
      }
      // Fan out the WhatsApp deep-link for each contact + an SMS fallback.
      const encoded = encodeURIComponent(data.body);
      for (const c of data.contacts) {
        window.open(`https://wa.me/${c.phone}?text=${encoded}`, "_blank", "noopener");
      }
      // SMS fallback via the first contact (iOS Safari won't spawn multiple
      // sms: sheets, so just seed one).
      const first = data.contacts[0];
      if (first) window.location.href = `sms:${first.phone}?body=${encoded}`;
      toast.success(`SOS sent to ${data.contacts.length} contact${data.contacts.length !== 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SOS failed");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={trigger} disabled={busy}
            className="bg-rose-600 hover:bg-rose-700 text-white rounded-full px-3 py-1.5 text-xs font-black inline-flex items-center gap-1 shadow-md disabled:opacity-60 animate-pulse"
            data-testid="rideshare-sos-btn">
      <AlertTriangle size={13}/> {busy ? "…" : "SOS"}
    </button>
  );
}


function EmergencyContactsCard() {
  const [items, setItems] = useState(null);
  const [form, setForm]   = useState({ name: "", phone: "" });
  const [busy, setBusy]   = useState(false);
  const [expanded, setExpanded] = useState(false);
  const load = () => api.get("/rideshare/emergency-contacts").then(r => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (!items) return null;
  const add = async () => {
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Name and phone required");
    setBusy(true);
    try {
      const { data } = await api.post("/rideshare/emergency-contacts", form);
      setItems(data.contacts || []);
      setForm({ name: "", phone: "" });
      toast.success("Contact saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const del = async (phone) => {
    try {
      const { data } = await api.delete(`/rideshare/emergency-contacts?phone=${encodeURIComponent(phone)}`);
      setItems(data.contacts || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="bg-white rounded-2xl p-3 shadow-sm mb-3" data-testid="rideshare-emergency-card">
      <button className="w-full flex items-center justify-between" onClick={() => setExpanded(x => !x)} data-testid="rideshare-emergency-toggle">
        <div className="flex items-center gap-2 text-rose-700 font-bold text-sm">
          <AlertTriangle size={16}/> Emergency contacts ({items.length}/3)
        </div>
        <span className="text-xs text-stone-400">{expanded ? "Hide" : "Manage"}</span>
      </button>
      {expanded && (
        <div className="mt-3 space-y-2">
          {items.map((c) => (
            <div key={c.phone} className="flex items-center justify-between bg-rose-50 rounded-xl px-3 py-2 text-sm" data-testid={`rideshare-emergency-item-${c.phone}`}>
              <div>
                <div className="font-bold text-stone-800">{c.name}</div>
                <div className="text-[11px] text-stone-500">+{c.phone}</div>
              </div>
              <button onClick={() => del(c.phone)} className="text-xs text-red-500 underline" data-testid={`rideshare-emergency-del-${c.phone}`}>Delete</button>
            </div>
          ))}
          {items.length < 3 && (
            <div className="grid grid-cols-5 gap-2 pt-1">
              <Input placeholder="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="col-span-2" data-testid="rideshare-emergency-name"/>
              <Input placeholder="Phone (with country code)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="col-span-2" data-testid="rideshare-emergency-phone"/>
              <Button onClick={add} disabled={busy} className="bg-rose-600 hover:bg-rose-700 font-bold" data-testid="rideshare-emergency-add">Add</Button>
            </div>
          )}
          <div className="text-[11px] text-stone-400 pt-1">Used by the SOS button to alert your loved ones via WhatsApp/SMS with your live location.</div>
        </div>
      )}
    </div>
  );
}


function AutoShareOnPayModal({ trip, onClose }) {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rideshare/trips/${trip.trip_id}/share`);
      const url = `${window.location.origin}${data.path}`;
      const text = `Following my ride live on Find2Near — ${trip.from_city} → ${trip.to_city}. ${url}`;
      let handled = false;
      if (navigator.share) {
        try { await navigator.share({ title: "My ride", text, url }); handled = true; } catch { /* cancel */ }
      }
      if (!handled && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied — send it to family.");
      } else if (!handled) {
        window.prompt("Copy this link:", url);
      }
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Share failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[75] bg-black/70 grid place-items-center p-4" onClick={onClose} data-testid="rideshare-autoshare-modal">
      <div className="bg-white rounded-2xl max-w-sm w-full p-5" onClick={e => e.stopPropagation()}>
        <div className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mb-2">
            <Send size={22}/>
          </div>
          <div className="font-black text-stone-800 text-lg">Ride confirmed!</div>
          <div className="text-xs text-stone-500 mt-1">Share your live ride with family so someone always knows where you are.</div>
        </div>
        <Button onClick={share} disabled={busy}
                className="mt-4 w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-black text-base"
                data-testid="rideshare-autoshare-send">
          {busy ? "…" : "Send my ride to family"}
        </Button>
        <button onClick={onClose} className="mt-2 w-full text-xs text-stone-400 underline" data-testid="rideshare-autoshare-skip">Skip for now</button>
      </div>
    </div>
  );
}


function AutoShareStickyBanner({ booking, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rideshare/trips/${booking.trip_id}/share`);
      const url = `${window.location.origin}${data.path}`;
      const text = `Following my ride live on Find2Near — ${url}`;
      let handled = false;
      if (navigator.share) {
        try { await navigator.share({ title: "My ride", text, url }); handled = true; } catch { /* cancel */ }
      }
      if (!handled && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied — send it to family.");
      } else if (!handled) {
        window.prompt("Copy this link:", url);
      }
      onDismiss?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Share failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-emerald-600 text-white rounded-2xl p-3 mb-3 flex items-center justify-between gap-3 shadow-md" data-testid="rideshare-autoshare-banner">
      <div className="flex items-center gap-2">
        <Send size={16}/>
        <div className="text-sm">
          <div className="font-bold">Ride paid — share it with family?</div>
          <div className="text-[11px] opacity-90">They'll see your live map · no login needed.</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={send} disabled={busy} className="bg-white text-emerald-700 hover:bg-emerald-50 font-black h-9 px-3" data-testid="rideshare-autoshare-banner-send">
          {busy ? "…" : "Send"}
        </Button>
        <button onClick={onDismiss} className="text-white/70 hover:text-white" data-testid="rideshare-autoshare-banner-dismiss">
          <X size={16}/>
        </button>
      </div>
    </div>
  );
}







// ─── Alerts ──────────────────────────────────────────────────────────────
function AlertsPanel({ user }) {
  const [items, setItems] = useState([]);
  const [form, setForm]   = useState({
    from_city: "", to_city: "", weekdays: [], max_price_inr: "",
    women_only: false, frequency: "instant",
  });
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const load = () => user && api.get("/rideshare/alerts").then(r => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);
  const toggleDay = (d) => setForm(f => ({ ...f, weekdays: f.weekdays.includes(d) ? f.weekdays.filter(x => x !== d) : [...f.weekdays, d] }));
  const save = async () => {
    if (!form.from_city || !form.to_city) return toast.error("From / To required");
    try {
      await api.post("/rideshare/alerts", {
        ...form,
        max_price_inr: form.max_price_inr ? parseInt(form.max_price_inr, 10) : null,
      });
      toast.success("Alert saved — you'll get an email when a matching ride is posted.");
      setForm({ from_city: "", to_city: "", weekdays: [], max_price_inr: "", women_only: false, frequency: "instant" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this alert?")) return;
    try { await api.delete(`/rideshare/alerts/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  if (!user) return <div className="text-center text-stone-400 py-8 text-sm">Sign in to save ride alerts.</div>;
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="rideshare-alert-form">
        <div className="text-lg font-bold text-stone-800">Save a ride alert</div>
        <div className="text-xs text-stone-500 mt-0.5">Get an email when a new carpool matches your route.</div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Input placeholder="From city*" value={form.from_city} onChange={e => upd("from_city", e.target.value)} data-testid="rideshare-alert-from"/>
          <Input placeholder="To city*"   value={form.to_city}   onChange={e => upd("to_city", e.target.value)}   data-testid="rideshare-alert-to"/>
          <Input type="number" placeholder="Max ₹/seat (optional)" value={form.max_price_inr} onChange={e => upd("max_price_inr", e.target.value)}/>
          <select value={form.frequency} onChange={e => upd("frequency", e.target.value)} className="h-10 px-3 rounded-md border border-input text-sm" data-testid="rideshare-alert-frequency">
            <option value="instant">Instant · email as soon as posted</option>
            <option value="daily">Daily digest · one email per day</option>
          </select>
        </div>
        <div className="mt-2">
          <div className="text-[10px] uppercase text-stone-500 font-black tracking-widest mb-1">Days of week (optional — leave blank for any day)</div>
          <div className="flex gap-1">
            {DAYS.map((d, i) => (
              <button key={d} onClick={() => toggleDay(i)}
                className={`flex-1 rounded-lg h-8 text-xs font-bold ${form.weekdays.includes(i) ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-700"}`}
                data-testid={`rideshare-alert-day-${d.toLowerCase()}`}>{d}</button>
            ))}
          </div>
        </div>
        <label className="mt-2 flex items-center gap-2 text-xs text-stone-600">
          <input type="checkbox" checked={form.women_only} onChange={e => upd("women_only", e.target.checked)}/>
          Only women-only trips
        </label>
        <Button onClick={save} className="mt-3 w-full h-10 bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="rideshare-alert-save">Save alert</Button>
      </div>

      <div className="space-y-2">
        <div className="text-xs uppercase text-stone-500 font-black tracking-widest">My alerts</div>
        {items.length === 0 && <div className="bg-white rounded-2xl p-6 text-center text-stone-400 text-sm">No alerts yet.</div>}
        {items.map(a => (
          <div key={a.alert_id} className="bg-white rounded-2xl p-3 shadow-sm flex items-center justify-between" data-testid={`rideshare-alert-${a.alert_id}`}>
            <div>
              <div className="font-bold text-stone-800">{a.from_city} → {a.to_city}</div>
              <div className="text-[11px] text-stone-500 mt-0.5">
                {a.frequency} · {a.max_price_inr ? `≤ ₹${a.max_price_inr}` : "any price"}
                {a.weekdays?.length ? ` · ${a.weekdays.map(d => DAYS[d]).join(", ")}` : ""}
                {a.women_only ? " · Women-only" : ""}
              </div>
            </div>
            <button onClick={() => del(a.alert_id)} className="text-xs text-red-500 underline" data-testid={`rideshare-alert-del-${a.alert_id}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}


function StatusPill({ s }) {
  const map = {
    published: "bg-emerald-100 text-emerald-700",
    started:   "bg-sky-100 text-sky-700",
    full:      "bg-stone-100 text-stone-700",
    cancelled: "bg-red-100 text-red-700",
    driver_cancelled: "bg-red-100 text-red-700",
    requested: "bg-amber-100 text-amber-700",
    approved:  "bg-sky-100 text-sky-700",
    paid_pending_approval: "bg-amber-100 text-amber-700",
    paid:      "bg-emerald-100 text-emerald-700",
    completed: "bg-emerald-600 text-white",
    refunded:  "bg-stone-100 text-stone-700",
  };
  const cls = map[s] || "bg-stone-100 text-stone-700";
  return <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full ${cls}`}>{s?.replace(/_/g," ")}</span>;
}
